import React, { useCallback, useState, useRef, useEffect } from 'react';
import { NavVisibilityContext } from './contexts/NavVisibilityContext';
import NavHelpWidget from './components/NavAssistant/NavHelpWidget';
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
// Register-interest page retired — no longer linked anywhere; use /register instead.
// import RegisterInterest from './components/RegisterInterest';
import AllLessonsFrontend from './components/AllLessonsFrontend';
import HomePageV2 from './components/HomePageV2';
import ChildrenDashboard from './components/ChildrenDashboard';
import QuizPage from './views/Quiz/Quiz';
import ChildTestResults from './components/ChildTestResults';
import CookiePolicy from './components/CookiePolicy';
import AboutPage from './components/AboutPage';

import LearningSession from './components/LearningSession';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Cookies from 'js-cookie';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
} from '@/components/shadcn/navigation-menu';
import { Button } from '@/components/shadcn/button';
import useCoreSkillsPendingRelease from '@/hooks/useCoreSkillsPendingRelease';
import usePendingAssignmentsCount from '@/hooks/usePendingAssignmentsCount';
import NavPendingBadge from '@/components/NavPendingBadge';
import { buildLoggedInNavItems } from '@/components/loggedInNavItems';
import { cn } from "@/lib/utils";
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

import TutorTuitionCall from './components/DailyVideoCalls/TuitionCall/TutorTuitionCall';
import TuitionCall from './components/DailyVideoCalls/TuitionCall/TuitionCall';
import TuitionCallTest from './components/DailyVideoCalls/TuitionCall/TuitionCallTest';

//import SubscriptionPlans from './components/SubscriptionPlans';
import LandingPageNew from './components/LandingPageNew';

import Logo from './media/Transparent-Star-Only-Logo.png';
import OnboardingFlow from './components/Onboarding/OnboardingFlow';
import StartTrial from './components/StartTrial';
import LoginLink from './components/LoginLink';
import ContinueOnboarding from './components/ContinueOnboarding';
import Watch from './components/Watch';
import WatchMembership from './components/WatchMembership';
import StaffSignup from './components/StaffSignup';
import ReferralSignup from './components/ReferralSignup';
import ReferAndEarn from './components/ReferAndEarn';
import UpcomingLessons from './components/UpcomingLessons';
import { SubscriptionManagement } from './components/SubscriptionManagement';
import StartPlatformSubscription from './components/StartPlatformSubscription';
import { Toaster } from './components/shadcn/toaster';
import TutorUpcomingLessons from './components/TutorUpcomingLessons';
import { Sheet, SheetContent, SheetTrigger } from "@/components/shadcn/sheet";
import { ChevronDown, Menu } from "lucide-react";
import StreakBadge from './components/StreakBadge';
import SparksBadge from './components/Sparks/SparksBadge';
import SparksCoachMark from './components/Sparks/SparksCoachMark';
import useShopAlert from './components/Sparks/useShopAlert';
import ShopBuySticker from './components/Sparks/ShopBuySticker';
import Shop from './components/Sparks/Shop';
import AchievementsPage from './components/Sparks/AchievementsPage';
import CelebrationQueue from './components/Sparks/CelebrationQueue';
import LevelChip from './components/Sparks/LevelChip';
import Leaderboard from './components/Sparks/Leaderboard';
import { SparksProvider } from './contexts/SparksContext';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
//import ProgressReports from './components/ProgressReports';
import Homework from './components/Homework';
import Worksheets from './components/Worksheets';
import CoreSkillsList from './views/core-skills/list';
import LearnCoreSkill from './views/core-skills/learn';
import CoreSkillsHomework from './components/CoreSkillsHomework';
import Dashboard from './components/Dashboard/Dashboard';
import DashboardCoreSkillRoute from './components/Dashboard/DashboardCoreSkillRoute';
import ReviewLandingPage from './components/Review/ReviewLandingPage';
// /blog and /blog/:slug are now served by the standalone Next.js blog app,
// proxied in via an Edge Middleware rewrite (see frontend/middleware.ts).

// Add near the top of your file, after imports
const TEST_MODE = process.env.REACT_APP_TEST_MODE === 'true';

function PrivateRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  const token = Cookies.get('token');
  const location = useLocation();

  if (!isAuthenticated && !token) {
    return <Navigate to="/" />;
  }

  // Simpler redirect logic
  if (user?.accessStatus === 'inactive' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" />;
  }

  return children;
}

function OnboardingRoute({ children }) {
  const { user } = useAuth();

  // If user has an active subscription, redirect to children-dashboard
  if (user?.accessStatus !== 'inactive') {
    return <Navigate to="/upcoming-lessons" />;
  }

  // Otherwise, show the onboarding flow
  return children;
}

function SubscriptionRequiredNotice() {
  const { user } = useAuth();

  // Platform-only users can self-serve restart their subscription by paying now —
  // the plan/price is recovered from their selectedPlan.
  const isPlatformPlan = ['platform_only', 'platform_only_family'].includes(user?.selectedPlan);

  if (user?.accessStatus === 'subscription_deleted') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold mb-4 text-red-800">Your free trial has ended</h2>
          {isPlatformPlan ? (
            <>
              <p className="text-red-700 text-lg mb-4">
                Start your subscription to regain access to the platform.
              </p>
              <Link to="/start-subscription">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Pay now to start subscription
                </Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-red-700 text-lg mb-4">
                Your subscription has been cancelled. To regain access to this content, please contact the Nova Learning team who will help reactivate your subscription.
              </p>
              <p className="text-red-600 text-sm">
                You can reach us at info@novalearning.co.uk or through our customer support channels.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <h2 className="text-2xl font-bold mb-4 text-amber-800">Subscription Required</h2>
        <p className="text-amber-700 text-lg mb-4">
          Please pay for your monthly subscription in order to access this content.
        </p>
        {isPlatformPlan && (
          <Link to="/start-subscription">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Pay now to start subscription
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function SubscriptionAccessGate({ children }) {
  const { user } = useAuth();
  if (user?.accessStatus !== 'has_access') {
    return <SubscriptionRequiredNotice />;
  }
  return children;
}

function TutorRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  const token = Cookies.get('token');
  const location = useLocation();

  if (!isAuthenticated && !token) {
    return <Navigate to="/login" />;
  }

  if (!user?.isTutor) {
    return <Navigate to="/upcoming-lessons" />;
  }

  return children;
}

function groupPendingCount(item) {
  return (item.children || []).reduce((sum, child) => sum + (child.badge?.count || 0), 0);
}

function LoggedInNavigationMenuBar() {
  const { user, logout, loggedInChild } = useAuth();
  const location = useLocation();
  const hasPendingCoreSkill = useCoreSkillsPendingRelease(loggedInChild?._id);
  const pendingAssignments = usePendingAssignmentsCount(loggedInChild?._id);
  const shopAlert = useShopAlert(loggedInChild?._id);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Local dropdown id — anchored under the trigger (not the shared NavigationMenu viewport).
  const [openGroupId, setOpenGroupId] = useState(null);

  const navItems = buildLoggedInNavItems({
    user,
    hasPendingCoreSkill,
    pendingAssignments,
    shopCanBuy: shopAlert.canBuy,
    testMode: TEST_MODE,
  });

  useEffect(() => {
    if (!openGroupId) return undefined;
    const onPointerDown = (e) => {
      if (!e.target?.closest?.(`[data-nav-group="${openGroupId}"]`)) {
        setOpenGroupId(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenGroupId(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openGroupId]);

  const handleLogout = () => {
    logout();
    // You might want to add navigation to the login page here
    // or handle it in your logout function
  };

  const isActive = (path) => {
    if (path === '/lets-learn') {
      // Consider '/', '/lesson/:lessonId', and '/quiz/:lessonId' as active for "Let's Learn!"
      return location.pathname === '/' ||
        location.pathname.startsWith('/lesson/') ||
        location.pathname.startsWith('/quiz/') ||
        location.pathname === '/lets-learn';
    }
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
    }
    return location.pathname === path;
  };

  const isGroupActive = (item) => item.children?.some((child) => isActive(child.href));

  const desktopNavLinkBase =
    'inline-flex h-full min-h-[2.75rem] w-full min-w-0 flex-col items-center justify-center self-stretch px-2 py-1.5 text-sm font-medium leading-snug rounded-md transition-colors duration-150 ease-in-out';

  const desktopTriggerClass = cn(
    desktopNavLinkBase,
    'h-auto w-full flex-row gap-0.5 bg-transparent hover:bg-white/10 focus:bg-white/10 focus:outline-none'
  );

  const renderMobileLink = (item) => (
    <a
      key={item.href}
      href={item.href}
      aria-label={item.ariaLabel}
      onClick={() => setMobileMenuOpen(false)}
      className={cn(
        'flex min-h-[44px] items-center justify-between rounded-md px-3 py-2 text-lg font-fredoka transition-colors duration-150',
        isActive(item.href)
          ? 'bg-[#E6DDFB] text-[#2F1E6B] font-semibold'
          : 'text-[#5C3BD1] hover:bg-[#F1EEF7]'
      )}
    >
      <span>{item.label}</span>
      {item.buySticker && <ShopBuySticker testId="shop-can-buy-mobile" />}
      {item.badge?.count > 0 && (
        <span
          data-testid={`${item.badge.testId}-mobile`}
          aria-hidden="true"
          className="ml-3 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#5C3BD1] px-2 text-xs font-bold text-white"
        >
          {item.badge.count}
        </span>
      )}
    </a>
  );

  const renderDesktopLink = (item) => (
    <NavigationMenuItem
      key={item.href}
      className={cn('min-w-0 max-w-none flex-1 basis-0', (item.badge || item.buySticker) && 'relative')}
    >
      <NavigationMenuLink
        className={cn(
          desktopNavLinkBase,
          isActive(item.href)
            ? 'bg-[#E6DDFB] text-[#2F1E6B] font-semibold'
            : 'text-white/80 hover:text-white hover:bg-white/10'
        )}
        href={item.href}
        aria-label={item.ariaLabel}
        data-sparks-tip={item.sparksTip}
      >
        <span className="line-clamp-2 w-full min-w-0 break-words text-center [overflow-wrap:anywhere]">
          {item.label}
        </span>
      </NavigationMenuLink>
      {item.badge && (
        <NavPendingBadge count={item.badge.count} testId={item.badge.testId} />
      )}
      {item.buySticker && <ShopBuySticker placement="corner" testId="shop-can-buy" />}
    </NavigationMenuItem>
  );

  const renderDesktopGroup = (item) => {
    const pending = groupPendingCount(item);
    const active = isGroupActive(item);
    const open = openGroupId === item.id;
    return (
      <NavigationMenuItem
        key={item.id}
        data-nav-group={item.id}
        className="relative min-w-0 max-w-none flex-1 basis-0"
      >
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={`${item.id}-menu`}
          onClick={() => setOpenGroupId(open ? null : item.id)}
          className={cn(
            desktopTriggerClass,
            active || open
              ? 'bg-[#E6DDFB] text-[#2F1E6B] font-semibold'
              : 'text-white/80 hover:text-white'
          )}
        >
          <span className="line-clamp-2 min-w-0 break-words text-center [overflow-wrap:anywhere]">
            {item.label}
          </span>
          <ChevronDown
            className={cn('h-3 w-3 shrink-0 transition-transform duration-200', open && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
        {pending > 0 && (
          <NavPendingBadge count={pending} testId={`${item.id}-pending-count`} />
        )}
        {/* Tip proxies so SparksCoachMark can find nested targets while the menu is closed. */}
        {item.children
          .filter((child) => child.sparksTip)
          .map((child) => (
            <span
              key={child.sparksTip}
              data-sparks-tip={child.sparksTip}
              className="pointer-events-none absolute inset-0 opacity-0"
              aria-hidden="true"
            />
          ))}
        {open && (
          <ul
            id={`${item.id}-menu`}
            role="menu"
            className="absolute left-0 top-full z-50 mt-1.5 flex min-w-[14rem] flex-col gap-0.5 rounded-md border border-[#E6DDFB] bg-white p-1 shadow-lg"
          >
            {item.children.map((child) => (
              <li key={child.href} role="none">
                <a
                  role="menuitem"
                  href={child.href}
                  aria-label={child.ariaLabel}
                  data-sparks-tip={child.sparksTip}
                  onClick={() => setOpenGroupId(null)}
                  className={cn(
                    'flex min-h-[40px] items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive(child.href)
                      ? 'bg-[#E6DDFB] text-[#2F1E6B] font-semibold'
                      : 'text-[#2F1E6B] hover:bg-[#F1EEF7]'
                  )}
                >
                  <span>{child.label}</span>
                  {child.badge?.count > 0 && (
                    <span
                      data-testid={`${child.badge.testId}-menu`}
                      aria-hidden="true"
                      className="ml-3 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#5C3BD1] px-1.5 text-[10px] font-bold text-white"
                    >
                      {child.badge.count}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        )}
      </NavigationMenuItem>
    );
  };

  return (
    <div className="w-full bg-[#5C3BD1] shadow-md font-poppins" data-sparks-nav>
      {loggedInChild && <SparksCoachMark childId={loggedInChild._id} announceCanBuy={shopAlert.announce} balance={shopAlert.balance} />}
      <div className="flex min-w-0 items-center gap-3 px-4 py-2.5">
        {/* Phone (< md): hamburger + sheet. Desktop/tablet render the tab bar below. */}
        <div className="flex min-w-0 flex-1 items-center md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-11 gap-2 px-2 text-white hover:bg-white/10 hover:text-white"
                data-sparks-menu
              >
                <Menu className="h-6 w-6" />
                <span className="text-base font-medium">Menu</span>
                {shopAlert.canBuy && <ShopBuySticker placement="corner" testId="shop-can-buy-menu" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] overflow-y-auto" data-sparks-nav>
              <nav className="mt-8 flex flex-col gap-1" aria-label="Main">
                {navItems.map((item) => {
                  if (!item.children) return renderMobileLink(item);
                  return (
                    <div key={item.id} className="mt-2 first:mt-0">
                      <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#5C3BD1]/70">
                        {item.label}
                        {groupPendingCount(item) > 0 && (
                          <span
                            data-testid={`${item.id}-pending-count-mobile`}
                            aria-hidden="true"
                            className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#5C3BD1] px-1.5 text-[10px] font-bold text-white"
                          >
                            {groupPendingCount(item)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 pl-2">
                        {item.children.map(renderMobileLink)}
                      </div>
                    </div>
                  );
                })}
              </nav>
              <hr className="my-4 border-gray-200" />
              <Button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                variant="outline"
                className="h-11 w-full bg-[#F37665] text-white hover:bg-[#e05a4a] hover:text-white"
              >
                Logout
              </Button>
            </SheetContent>
          </Sheet>
        </div>

        <div className="hidden min-h-0 min-w-0 flex-1 md:block">
        <NavigationMenu className="relative z-10 flex h-full w-full min-w-0 max-w-none flex-none items-stretch justify-start">
          <NavigationMenuList className="flex w-full min-w-0 flex-nowrap items-stretch justify-start gap-1">
            {navItems.map((item) => (item.children ? renderDesktopGroup(item) : renderDesktopLink(item)))}
          </NavigationMenuList>
        </NavigationMenu>
        </div>
        {loggedInChild && (
          <div className="shrink-0 mx-1">
            <SparksBadge />
          </div>
        )}
        {loggedInChild && (
          <div className="shrink-0 mx-1">
            <LevelChip />
          </div>
        )}
        {loggedInChild && (
          <div className="shrink-0 mx-1">
            <StreakBadge />
          </div>
        )}
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="shrink-0 bg-[#F37665] text-white hover:bg-[#e05a4a] transition-colors duration-150 ease-in-out"
        >
          Logout
        </Button>
      </div>
    </div>
  );
}

function LoggedOutNavigationMenuBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navItems = [
    { name: "How it works?", id: "how-it-works" },
    { name: "Reviews", id: "reviews" },
    { name: "Pricing", id: "pricing" },
    { name: "FAQs", id: "faqs" },
    { name: "Contact us", id: "contact-us" }
  ];

  const scrollToSection = (sectionId) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/?section=' + sectionId);
    }
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // Check if we're on login or register pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/cookie-policy';
  const isAboutPage = location.pathname === '/about';

  return (
    <header className="w-full h-24 bg-white shadow-sm border-b border-[#F1EEF7]">
      <div className="w-full h-full flex items-center">
        {/* Logo - Always at screen left */}
        <div className="flex items-center px-4 md:px-6 lg:px-8">
          <Link to="/" className="flex-shrink-0">
            <div className="h-12 w-12 transform transition-transform duration-200 hover:scale-110">
              <img src={Logo} alt="Nova Learning" className="h-full w-full object-contain" />
            </div>
          </Link>
        </div>

        {/* Center navigation - Centered with max-width */}
        <div className="flex-1 max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8">
          <nav className="h-11 flex items-center justify-center">

            {/* Desktop Navigation - Center (Hidden on auth pages) */}
            {!isAuthPage && (
              <div className="hidden lg:flex items-center space-x-10">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-[#5C3BD1]/80 hover:text-[#2F1E6B] transition-colors duration-200 text-base cursor-pointer font-fredoka"
                  >
                    {item.name}
                  </button>
                ))}
                <Link
                  to="/about"
                  className={`${isAboutPage ? 'text-[#2F1E6B]' : 'text-[#5C3BD1]/80'} hover:text-[#2F1E6B] transition-colors duration-200 text-base cursor-pointer font-fredoka`}
                >
                  About us
                </Link>
                <a
                  href="https://blog.novalearning.co.uk"
                  className="text-[#5C3BD1]/80 hover:text-[#2F1E6B] transition-colors duration-200 text-base cursor-pointer font-fredoka"
                >
                  Blog
                </a>
              </div>
            )}

          </nav>
        </div>

        {/* Auth Buttons & Mobile Menu - Always at screen right */}
        <div className="flex items-center px-4 md:px-6 lg:px-8">
          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              onClick={() => navigate('/login')}
              className="bg-[#5C3BD1] border-[#5C3BD1] text-white hover:bg-[#2F1E6B] hover:border-[#2F1E6B] rounded-lg px-6 py-2 font-fredoka font-medium"
            >
              Login
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="bg-white border-[#5C3BD1] text-[#5C3BD1] hover:bg-[#F7FAEE] rounded-lg px-6 py-2 font-fredoka font-medium"
              variant="outline"
            >
              Book Free Assessment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px]">
              <div className="flex flex-col gap-6 mt-8">
                {/* Mobile Auth Buttons at Top */}
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-[#5C3BD1] border-[#5C3BD1] text-white hover:bg-[#2F1E6B] hover:border-[#2F1E6B] rounded-lg py-3 font-fredoka"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/register');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-white border-[#5C3BD1] text-[#5C3BD1] hover:bg-[#F7FAEE] rounded-lg py-3 font-fredoka"
                    variant="outline"
                  >
                    Book Free Assessment
                  </Button>
                </div>

                {/* Navigation Items (Hidden on auth pages) */}
                {!isAuthPage && (
                  <>
                    {/* Divider */}
                    <hr className="border-gray-200" />

                    {/* Mobile Navigation Items */}
                    <nav className="flex flex-col gap-4">
                      {navItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className="text-left text-[#5C3BD1] hover:text-[#2F1E6B] transition-colors duration-200 py-2 text-lg font-fredoka"
                        >
                          {item.name}
                        </button>
                      ))}
                      <Link
                        to="/about"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-left text-[#5C3BD1] hover:text-[#2F1E6B] transition-colors duration-200 py-2 text-lg font-fredoka"
                      >
                        About us
                      </Link>
                      <a
                        href="https://blog.novalearning.co.uk"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-left text-[#5C3BD1] hover:text-[#2F1E6B] transition-colors duration-200 py-2 text-lg font-fredoka"
                      >
                        Blog
                      </a>
                    </nav>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function OnlyLogoutButton() {
  const { logout } = useAuth();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    // You might want to add navigation to the login page here
    // or handle it in your logout function
  };

  const isActive = (path) => {
    if (path === '/lets-learn') {
      // Consider '/', '/lesson/:lessonId', and '/quiz/:lessonId' as active for "Let's Learn!"
      return location.pathname === '/' ||
        location.pathname.startsWith('/lesson/') ||
        location.pathname.startsWith('/quiz/');
    }
    return location.pathname === path;
  };

  return (
    <div className="w-full bg-[#5C3BD1] shadow-md font-poppins">
      <div className="flex justify-end items-center px-4 py-2">
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="bg-[#F37665] text-white hover:bg-[#e05a4a] transition-colors duration-150 ease-in-out ml-4"
        >
          Logout
        </Button>
      </div>
    </div>
  );
}



function AppContent() {
  const { isAuthenticated, user } = useAuth();
  const token = Cookies.get('token');
  // Set true by a classroom question view (QuestionZoom) while mounted, so the
  // top nav is hidden only during in-classroom questions.
  const [navHidden, setNavHidden] = useState(false);
  // Set true by the live call on phones for the whole call (see NavVisibilityContext).
  const [callNavHidden, setCallNavHidden] = useState(false);
  // How many working screens with the child's Addy are mounted (useHideAskAddy).
  const [askAddyHiders, setAskAddyHiders] = useState(0);
  const hideAskAddy = useCallback((on) => setAskAddyHiders((n) => Math.max(0, n + (on ? 1 : -1))), []);

  // Expose the nav's current height as --nav-height so layouts (e.g. the lesson
  // teaching step) can size themselves to "viewport minus nav" exactly. Updates
  // when the nav shows/hides/wraps. 0 when the nav isn't rendered.
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const setVar = () =>
      document.documentElement.style.setProperty('--nav-height', `${el.offsetHeight}px`);
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Router>
      {/* Site-wide defaults; pages with their own <Helmet> (e.g. /about) override these. */}
      <Helmet>
        <title>Nova Learning</title>
        <meta
          name="description"
          content="Fun online Maths classes for UK kids in Years 2–6, with live tutors, AI support, instant feedback & weekly progress reports."
        />
      </Helmet>
      <NavVisibilityContext.Provider value={{ navHidden, setNavHidden, setCallNavHidden, askAddyHidden: askAddyHiders > 0, hideAskAddy }}>
      <div id="app-scroll-container">
        {/* deal with all the relevant navigation menu logic here*/}
        <div ref={navRef}>
        {(isAuthenticated || token) && user?.accessStatus === 'inactive' && <OnlyLogoutButton />}
        {(isAuthenticated || token) && user?.accessStatus !== 'inactive' && !navHidden && !callNavHidden && <LoggedInNavigationMenuBar />}
        {!(isAuthenticated || token) && <LoggedOutNavigationMenuBar />}
        </div>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated || token ? (
                <Navigate to="/children-dashboard" />
              ) : (
                /*<LandingPage /> */
                <LandingPageNew />
              )
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Facebook lead funnel: parent confirms their email, gets logged in, then onboards. */}
          <Route path="/start-trial" element={<StartTrial />} />
          {/* Welcome WhatsApp login link — redeems the single-use code, then routes on. */}
          <Route path="/i/:code" element={<LoginLink />} />
          {/* Welcome WhatsApp video link — static walkthrough video, same URL for everyone. */}
          <Route path="/watch" element={<Watch />} />
          <Route path="/watch/membership" element={<WatchMembership />} />
          {/* Onboarding-nudge WhatsApp link — reusable token, logs the lead straight into onboarding. */}
          <Route path="/c/:code" element={<ContinueOnboarding />} />
          {/* Staff-assisted signup (secret link). Creates the customer account, then onboards. */}
          <Route path="/staff-signup" element={<StaffSignup />} />
          <Route path="/r/:code" element={<ReferralSignup />} />
          <Route
            path="/refer"
            element={
              <PrivateRoute>
                <ReferAndEarn />
              </PrivateRoute>
            }
          />
          {/* Register-interest page retired — no longer linked anywhere; use /register instead.
          <Route path="/register-interest" element={<RegisterInterest />} /> */}
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          {/* Public About page — logged-in users go to their dashboard, like "/". */}
          <Route
            path="/about"
            element={isAuthenticated || token ? <Navigate to="/children-dashboard" /> : <AboutPage />}
          />
          <Route
            path="/lets-learn"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <AllLessonsFrontend />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/lesson/:lessonId"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <HomePageV2 resolvedTrack="home" />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/quiz/:topicId/:lessonId"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <QuizPage />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/children-dashboard"
            element={
              <PrivateRoute>
                <ChildrenDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/shop"
            element={
              <PrivateRoute>
                <Shop />
              </PrivateRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <PrivateRoute>
                <Leaderboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/achievements"
            element={
              <PrivateRoute>
                <AchievementsPage />
              </PrivateRoute>
            }
          />
          {/*<Route 
            path="/progress-reports" 
            element={
              <PrivateRoute>
                <ProgressReports />
              </PrivateRoute>
            } 
          />*/}
          <Route
            path="/homework"
            element={
              <PrivateRoute>
                <Homework />
              </PrivateRoute>
            }
          />
          <Route
            path="/worksheets"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <Worksheets />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
             <Route
            path="/core-skills-homework"
            element={
              <PrivateRoute>
                <CoreSkillsHomework />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <Dashboard />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/core-skill/:lessonId"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <DashboardCoreSkillRoute />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/child-test-results"
            element={
              <PrivateRoute>
                <ChildTestResults />
              </PrivateRoute>
            }
          />
          <Route
            path="/tuition-session"
            element={
              <PrivateRoute>
                <TuitionCallTest />
              </PrivateRoute>
            }
          />
          <Route
            path="/learning-session"
            element={
              <PrivateRoute>
                <LearningSession />
              </PrivateRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/review" element={<ReviewLandingPage />} />
          {/*<Route 
            path="/subscription" 
            element={
              <PrivateRoute>
                <SubscriptionPlans />
              </PrivateRoute>
            } 
          />*/}
          <Route
            path="/onboarding"
            element={
              <PrivateRoute>
                <OnboardingRoute>
                  <OnboardingFlow />
                </OnboardingRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/start-subscription"
            element={
              <PrivateRoute>
                <StartPlatformSubscription />
              </PrivateRoute>
            }
          />
          <Route
            path="/upcoming-lessons"
            element={
              <PrivateRoute>
                {/*<UpcomingLessons />*/}
                <TuitionCall />
              </PrivateRoute>
            }
          />
          <Route
            path="/manage-subscription"
            element={
              <PrivateRoute>
                <SubscriptionManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/tutor-upcoming-lessons"
            element={
              <PrivateRoute>
                <TutorRoute>
                  <TutorTuitionCall />
                </TutorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/core-skills"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <CoreSkillsList />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/core-skills/learn/:lessonId"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <LearnCoreSkill />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/core-skills/quiz/:topicId/:lessonId"
            element={
              <PrivateRoute>
                <SubscriptionAccessGate>
                  <QuizPage isCoreSkill={true} />
                </SubscriptionAccessGate>
              </PrivateRoute>
            }
          />
        </Routes>
        <NavHelpWidget />
      </div>
      </NavVisibilityContext.Provider>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <SparksProvider>
      <AppContent />
      <CelebrationQueue />
      <Toaster />
    </SparksProvider>
    </AuthProvider>
  );
}

export default App;



